/**
 * Generische Karten-Section
 * Wiederverwendbar auf verschiedenen Pages (Fläche, Anlagen, etc.)
 *
 * Verantwortlich für:
 * - Darstellung der Karte mit Section-Wrapper und Titel
 * - Picker-Modus wenn keine Geometrie vorhanden: Kartenklick → Koordinate bestätigen → speichern
 * - "Fläche hochladen"-Button unten-Mitte wenn keine Geometrie
 * - Shapefile- und ALKIS-Upload (Dialoge + API-Aufrufe)
 * - Loading-Overlay während Upload
 *
 * Seiten-spezifische Logik (Parcels, Status-Filter) wird über Props gesteuert.
 */
import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { CloudUpload as CloudUploadIcon } from "@mui/icons-material";
import Section from "../layout/Section";
import UploadDialog from "../common/overlays/UploadDialog";
import MapView from "./MapView";
import MapBottomBar from "./components/MapBottomBar";
import Button from "../common/inputs/Button";
import { GeoJSON, GenerationUnit } from "../../types";
import { ParcelData } from "./types";
import { apiService } from "../../services/api";
import { generationUnitsApi } from "../../services/api/generationUnits";
import { parcelsApi } from "../../services/api/parcels";
import { useToast } from "../../contexts/ToastContext";
import { useProjectParcels } from "./hooks/useProjectParcels";
import { useProjectAnlagen } from "./hooks/useProjectAnlagen";
import { GERMANY_CENTER } from "../../utils/geometry";
import { colors } from "../../styles/colors";
import { fonts } from "../../styles/theme";

interface MapSectionProps {
  /** Projekt-ID für Upload-API-Aufrufe */
  projectId: string;
  /** Projektname für die Karte */
  projectName: string;
  /** GeoJSON-Geometrie der Windpark-Fläche */
  geometrie?: GeoJSON;
  /** Area-ID (aktiviert ALKIS-Upload wenn gesetzt) */
  areaId?: string;
  /** Windenergieanlagen (Override — wenn gesetzt, wird internes Laden übersprungen) */
  anlagen?: GenerationUnit[];
  /** Kartenhöhe (CSS-Wert); ohne Angabe füllt die Karte den sichtbaren Bereich */
  height?: string;
  /** Titel über der Section; ohne Angabe steht dort nur der titleSuffix */
  title?: string;
  /** Zusatzinhalt im Section-Header (Kennzahlen-Badges) */
  titleSuffix?: React.ReactNode;
  /** Section aufklappbar */
  collapsible?: boolean;
  /** Callback nach erfolgreichem Geometrie-Upload — Page entscheidet was neu geladen wird */
  onGeometrieChanged?: () => Promise<void> | void;
  /** Gespeicherter Standort (ohne Fläche) */
  standortKoordinate?: { breitengrad: number; laengengrad: number };
  /** Callback wenn Standort per Kartenklick gesetzt wurde */
  onStandortSaved?: (breitengrad: number, laengengrad: number) => Promise<void>;

  /** Flurstücke auf der Karte (Override — wenn gesetzt, wird internes Laden übersprungen) */
  parcels?: ParcelData[];
  /** Ausgewähltes Flurstück (Highlighting) */
  selectedParcelId?: string;
  /** Ausgewählte Anlage (Highlighting) – dieselbe ID, die `onAnlageSelect` meldet */
  selectedAnlageId?: string;
  /** Callback bei Flurstück-Klick */
  onParcelClick?: (parcelId: string) => void;
  /** Callback bei Flurstück-Update */
  onParcelUpdate?: (updatedParcel: any) => void;
  /** Callback wenn eine Anlage per Drag&Drop verschoben wird */
  onAnlagePositionChange?: (
    unitId: string,
    breitengrad: number,
    laengengrad: number,
  ) => void | Promise<void>;
  /** Callback wenn eine Anlage angeklickt oder gezogen wird */
  onAnlageSelect?: (unitId: string) => void;
  /** Zähler: bei Änderung werden interne Anlagen-Daten neu geladen (z.B. nach Owner-Status-Änderung) */
  anlagenRefreshTrigger?: number;
}

const MapSection: React.FC<MapSectionProps> = ({
  projectId,
  projectName,
  geometrie,
  areaId,
  anlagen,
  height = "calc(100vh - 400px)",
  title,
  titleSuffix,
  collapsible = false,
  onGeometrieChanged,
  standortKoordinate,
  onStandortSaved,
  parcels,
  selectedParcelId,
  selectedAnlageId,
  onParcelClick,
  onParcelUpdate,
  onAnlagePositionChange,
  onAnlageSelect,
  anlagenRefreshTrigger,
}) => {
  const { showSuccess, showError } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [shapefileDialogOpen, setShapefileDialogOpen] = useState(false);
  const [alkisDialogOpen, setAlkisDialogOpen] = useState(false);
  const [pendingCoordinate, setPendingCoordinate] = useState<
    [number, number] | null
  >(null);

  const hasExternalAnlagen = anlagen !== undefined;
  const {
    anlagen: internalAnlagen,
    reload: reloadAnlagen,
    patchPosition,
  } = useProjectAnlagen(hasExternalAnlagen ? undefined : projectId);

  const [worstStatusOverrides, setWorstStatusOverrides] = useState<
    Record<string, string | undefined>
  >({});

  const activeAnlagen = useMemo(() => {
    const base = anlagen ?? internalAnlagen;
    if (!hasExternalAnlagen || Object.keys(worstStatusOverrides).length === 0)
      return base;
    return base.map((a) => ({
      ...a,
      worst_parcel_status:
        a.id in worstStatusOverrides
          ? worstStatusOverrides[a.id]
          : a.worst_parcel_status,
    }));
  }, [anlagen, internalAnlagen, hasExternalAnlagen, worstStatusOverrides]);

  const hasExternalParcels = parcels !== undefined;
  const { parcels: internalParcels, reload: reloadParcels } = useProjectParcels(
    hasExternalParcels ? undefined : projectId,
  );

  const [relevanceOverrides, setRelevanceOverrides] = useState<
    Record<string, boolean>
  >({});

  const prevTriggerRef = useRef(anlagenRefreshTrigger);
  useEffect(() => {
    if (
      anlagenRefreshTrigger !== undefined &&
      anlagenRefreshTrigger !== prevTriggerRef.current
    ) {
      prevTriggerRef.current = anlagenRefreshTrigger;
      if (!hasExternalAnlagen) reloadAnlagen();
    }
  }, [anlagenRefreshTrigger, hasExternalAnlagen, reloadAnlagen]);

  const activeParcels = useMemo(() => {
    const base = parcels ?? internalParcels;
    if (!hasExternalParcels || Object.keys(relevanceOverrides).length === 0)
      return base;
    return base.map((p) => ({
      ...p,
      relevant:
        p.id in relevanceOverrides ? relevanceOverrides[p.id] : p.relevant,
    }));
  }, [parcels, internalParcels, hasExternalParcels, relevanceOverrides]);

  const hasGeometrie = !!geometrie;
  const hasStandort = !!standortKoordinate;

  // Kartenposition: Standort > Fallback Deutschland
  const mapCoordinates: [number, number] | undefined = !hasGeometrie
    ? hasStandort
      ? [standortKoordinate!.breitengrad, standortKoordinate!.laengengrad]
      : GERMANY_CENTER
    : undefined;

  const mapZoom = !hasGeometrie ? (hasStandort ? 12 : 6) : undefined;

  // Standort-Marker anzeigen wenn kein Polygon aber Koordinaten vorhanden
  const standortMarker: [number, number] | undefined =
    !hasGeometrie && hasStandort
      ? [standortKoordinate!.breitengrad, standortKoordinate!.laengengrad]
      : undefined;

  const handleAnlagePositionChange = useCallback(
    async (unitId: string, breitengrad: number, laengengrad: number) => {
      // Fehler hier muessen sichtbar werden und weitergereicht bleiben: der Layer
      // setzt die Anlage nur dann auf die gespeicherte Position zurueck.
      try {
        if (onAnlagePositionChange) {
          await onAnlagePositionChange(unitId, breitengrad, laengengrad);
        } else {
          await generationUnitsApi.updateGenerationUnit(projectId, unitId, {
            breitengrad,
            laengengrad,
          });
          if (!hasExternalAnlagen)
            patchPosition(unitId, breitengrad, laengengrad);
        }
      } catch (error) {
        showError("Position konnte nicht gespeichert werden");
        throw error;
      }

      try {
        if (!hasExternalParcels) {
          await reloadParcels();
        } else {
          const freshParcels = await parcelsApi.getParcels(projectId);
          const overrides: Record<string, boolean> = {};
          freshParcels.forEach((p) => {
            overrides[p.id] = p.relevant ?? false;
          });
          setRelevanceOverrides(overrides);
        }

        if (!hasExternalAnlagen) {
          await reloadAnlagen();
        } else {
          const freshAnlagen =
            await generationUnitsApi.getProjectGenerationUnits(projectId);
          const statusOverrides: Record<string, string | undefined> = {};
          freshAnlagen.forEach((a) => {
            statusOverrides[a.id] = a.worst_parcel_status;
          });
          setWorstStatusOverrides(statusOverrides);
        }
      } catch {
        // Daten werden beim nächsten Laden aktualisiert
      }
    },
    [
      onAnlagePositionChange,
      hasExternalAnlagen,
      hasExternalParcels,
      projectId,
      reloadParcels,
      reloadAnlagen,
      patchPosition,
      showError,
    ],
  );

  const handleShapefileUpload = useCallback(
    async (file: File) => {
      setIsUploading(true);
      try {
        const result = await apiService.uploadShapefile(file);

        let geo = result.geometrie;
        if (!geo && result.flaeche) {
          const coordinates = result.flaeche.map((c: any) => [
            c.laengengrad,
            c.breitengrad,
          ]);
          geo = { type: "Polygon", coordinates: [coordinates] };
        }

        await apiService.updateProjectArea(projectId, { geometrie: geo });
        showSuccess("Fläche erfolgreich importiert");
        if (!hasExternalAnlagen) await reloadAnlagen();
        await onGeometrieChanged?.();
      } catch (error: any) {
        showError(error.message || "Fehler beim Verarbeiten des Shapefiles");
      } finally {
        setIsUploading(false);
      }
    },
    [
      projectId,
      onGeometrieChanged,
      showSuccess,
      showError,
      hasExternalAnlagen,
      reloadAnlagen,
    ],
  );

  const handleAlkisUpload = useCallback(
    async (file: File) => {
      setIsUploading(true);
      try {
        const area = await apiService.getProjectArea(projectId);
        const result = await apiService.importAlkisOwners(
          projectId,
          area.id,
          file,
        );

        const message = `ALKIS-Import erfolgreich: ${result.imported_parcels || 0} Flurstücke, ${result.imported_owners || 0} Eigentümer`;
        showSuccess(message);

        if (!hasExternalParcels) await reloadParcels();
        await onGeometrieChanged?.();
      } catch (error: any) {
        showError(error.message || "Fehler beim ALKIS-Import");
      } finally {
        setIsUploading(false);
      }
    },
    [
      projectId,
      onGeometrieChanged,
      hasExternalParcels,
      reloadParcels,
      showSuccess,
      showError,
    ],
  );

  // Picker-Modus ist aktiv wenn keine Geometrie vorhanden
  const handleMapClick = useCallback(
    (lat: number, lon: number) => {
      if (!hasGeometrie && onStandortSaved) {
        setPendingCoordinate([lat, lon]);
      }
    },
    [hasGeometrie, onStandortSaved],
  );

  const handleStandortBestaetigen = useCallback(async () => {
    if (!pendingCoordinate || !onStandortSaved) return;
    try {
      await onStandortSaved(pendingCoordinate[0], pendingCoordinate[1]);
      await onGeometrieChanged?.();
    } catch {
      showError("Standort konnte nicht gespeichert werden");
    } finally {
      setPendingCoordinate(null);
    }
  }, [pendingCoordinate, onStandortSaved, onGeometrieChanged, showError]);

  const handleStandortAbbrechen = useCallback(() => {
    setPendingCoordinate(null);
  }, []);

  const sectionHeader = titleSuffix ? (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {title && (
        <Typography
          variant="h6"
          sx={{ fontWeight: fonts.weights.light, color: "text.primary" }}
        >
          {title}
        </Typography>
      )}
      {titleSuffix}
    </Box>
  ) : undefined;

  return (
    <>
      <Section
        title={sectionHeader ? undefined : title}
        headerContent={sectionHeader}
        noInnerPadding={true}
        collapsible={collapsible}
      >
        <Box sx={{ position: "relative" }}>
          <MapView
            projectName={projectName}
            coordinates={mapCoordinates}
            zoom={mapZoom}
            geometrie={geometrie}
            anlagen={activeAnlagen}
            height={height}
            showLabel={false}
            showBorder={false}
            noPadding={true}
            parcels={activeParcels}
            onParcelUpdate={onParcelUpdate}
            onParcelClick={onParcelClick}
            selectedParcelId={selectedParcelId}
            selectedAnlageId={selectedAnlageId}
            showMenuButton={hasGeometrie}
            onShapefileUploadClick={
              hasGeometrie ? () => setShapefileDialogOpen(true) : undefined
            }
            onAlkisUploadClick={
              hasGeometrie && areaId
                ? () => setAlkisDialogOpen(true)
                : undefined
            }
            onAnlagePositionChange={handleAnlagePositionChange}
            onAnlageSelect={onAnlageSelect}
            onMapClick={
              !hasGeometrie && onStandortSaved ? handleMapClick : undefined
            }
            standortLocation={pendingCoordinate ?? standortMarker}
          />

          {/* Fläche hochladen — unten Mitte wenn kein Polygon und kein pending */}
          {!hasGeometrie && !pendingCoordinate && (
            <MapBottomBar>
              <Button
                variant="secondary"
                startIcon={<CloudUploadIcon />}
                onClick={() => setShapefileDialogOpen(true)}
                sx={{ px: 3, py: 1 }}
              >
                Fläche hochladen
              </Button>
            </MapBottomBar>
          )}

          {/* Bestätigungsleiste nach Kartenklick */}
          {pendingCoordinate && (
            <MapBottomBar>
              <Typography variant="body2">Standort hier setzen?</Typography>
              <Button
                variant="primary"
                onClick={handleStandortBestaetigen}
                sx={{ py: 0.5, px: 1.5, fontSize: "0.8rem" }}
              >
                Bestätigen
              </Button>
              <Button
                variant="secondary"
                onClick={handleStandortAbbrechen}
                sx={{ py: 0.5, px: 1.5, fontSize: "0.8rem" }}
              >
                Abbrechen
              </Button>
            </MapBottomBar>
          )}

          {isUploading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: colors.glass.backgroundSolid,
                zIndex: 1000,
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <CircularProgress size={40} />
                <Typography
                  variant="body2"
                  sx={{ mt: 2, color: "text.secondary" }}
                >
                  Wird verarbeitet...
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Section>

      <UploadDialog
        open={shapefileDialogOpen}
        onClose={() => setShapefileDialogOpen(false)}
        onUpload={handleShapefileUpload}
        acceptedFileTypes=".zip"
        title="Fläche hochladen"
        acceptedTypesHint="ZIP-Archiv mit Shapefile (.zip)"
      />

      {areaId && (
        <UploadDialog
          open={alkisDialogOpen}
          onClose={() => setAlkisDialogOpen(false)}
          onUpload={handleAlkisUpload}
          acceptedFileTypes=".xml"
          title="Flurstücke hochladen (ALKIS)"
          dragDropText="ALKIS XML-Datei hierher ziehen"
          acceptedTypesHint="Nur .xml Dateien im ALKIS-NAS-Format"
        />
      )}
    </>
  );
};

export default MapSection;
